/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useRef, useState } from 'react';
import { Grid, Fab, makeStyles, Collapse } from '@material-ui/core';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import uniqueId from 'lodash/uniqueId';

import DefaultCard from '../DefaultCard';
import { colors } from '../../../theme';

import ImagePreview from '../ImagePreview';
import PdfPreview from '../PdfPreview';
import ConfirmationDialog from '../ConfirmationDialog';

// Helpers
import {
  isImage,
  isPdf,
  processFileBlob,
  isValidSize,
  compressImage,
} from '../../../functions/helpers';
import { ResizerOptions } from '../../../functions/helpers/compressImage';
import MessageBox from '../MessageBox';

export type Mode = 'local' | 'cameraonly';

type Props = {
  title: string;
  subTitle: string;
  confirmationMessage: string;
  /** It also accepts URL string */
  base64String: string;
  onTakePhotoRequested?: () => void;
  onLocalImageCaptured?: (fileName: string, newImage: string) => void;
  mode?: Mode;
  disabled?: boolean;
  capturerOptions?: ResizerOptions;
  fileName?: string;
  id?: string;
  deleteButtonId?: string;
  requestStatus?: string;
};

/**
 * Processes a pdf file and notifies the base64 result
 *
 * @param file - The file captured by the input
 * @param onLocalImageCaptured - Callback called when the processing finishes
 * @param setShowWarning - Setter for the "warning" label
 */
async function processPdf(
  file: File,
  onLocalImageCaptured: (name: string, base64: string) => void,
  setShowWarning: (valid: boolean) => void,
): Promise<void> {
  const result = await processFileBlob(file);
  const valid = isValidSize(file.size);
  setShowWarning(!valid);
  if (valid) {
    onLocalImageCaptured(result.name, result.base64);
  }
}

/**
 * Processes an image file and notifies the base64 result.
 *
 * @param image - The file captured by the input
 * @param onLocalImageCaptured - Callback called when the processing finishes
 * @param setShowWarning - Setter for the "warning" label
 * @param capturerOptions - Custom overwrite capture options for the image compressor
 */
async function processImage(
  image: File,
  onLocalImageCaptured: (name: string, base64: string) => void,
  setShowWarning: (valid: boolean) => void,
  capturerOptions: ResizerOptions,
): Promise<void> {
  if (onLocalImageCaptured) {
    const resizedImage = await compressImage(image, capturerOptions);
    onLocalImageCaptured(image.name, resizedImage);
  }
  setShowWarning(false);
}

const useStyles = makeStyles({
  hiddenInput: {
    display: 'none',
  },
  pictureFab: {
    backgroundColor: colors.bright_turquoise,
    boxShadow: 'none',
  },
  button: {
    color: colors.bright_turquoise,
    marginLeft: '0.5rem',
  },
});

/**
 * PhotoCard will by default check for image mimeTypes. If not image, it will
 * assume it is on pdf format.
 */
const PhotoCard = ({
  title,
  subTitle,
  confirmationMessage,
  base64String,
  onTakePhotoRequested,
  onLocalImageCaptured,
  mode = 'local',
  disabled = false,
  fileName = '',
  capturerOptions,
  id = '',
  deleteButtonId = '',
  requestStatus = 'initial',
}: Props): JSX.Element => {
  const classes = useStyles();
  const uniqueIdRef = useRef<string>(uniqueId('photo_card_')).current;
  const hasImageMime = isImage(base64String);
  const hasPdfMime = isPdf(base64String);
  const [showWarning, setShowWarning] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  function onDelete(): void {
    setShowConfirmation(true);
  }

  function handleDeleteConfirm(): void {
    onLocalImageCaptured('', '');
    setShowConfirmation(false);
  }

  function handleDeleteReject(): void {
    setShowConfirmation(false);
  }

  const uploadButton = useMemo((): JSX.Element => {
    function handleFileInputChange(
      e: React.ChangeEvent<HTMLInputElement>,
    ): void {
      if (!e.target.files || e.target.files.length <= 0) {
        return;
      }
      const firstFile = e.target.files[0];
      // determines mime type. If not pdf, assume by default we are dealing with image.
      if (firstFile.type === 'application/pdf') {
        processPdf(firstFile, onLocalImageCaptured, setShowWarning);
      } else {
        processImage(
          firstFile,
          onLocalImageCaptured,
          setShowWarning,
          capturerOptions,
        );
      }
    }

    return mode === 'cameraonly' ? (
      <Fab
        id={`${id}-icon`}
        data-testid="take-photo-button"
        color="primary"
        onClick={onTakePhotoRequested}
        disabled={disabled}
      >
        <CameraAltIcon />
      </Fab>
    ) : (
      <>
        <input
          data-testid="file-input"
          id={uniqueIdRef}
          type="file"
          onChange={handleFileInputChange}
          accept="image/*, application/pdf"
          className={classes.hiddenInput}
        />
        <label data-testid="local-label" htmlFor={uniqueIdRef}>
          <Fab
            id={`${id}-icon`}
            data-testid="take-local-photo-button"
            color="primary"
            disabled={disabled}
            component="span"
            className={classes.pictureFab}
          >
            <CameraAltIcon />
          </Fab>
        </label>
      </>
    );
  }, [
    mode,
    uniqueIdRef,
    disabled,
    classes,
    onLocalImageCaptured,
    onTakePhotoRequested,
  ]);

  return (
    <Grid container alignContent="stretch">
      <Grid item xs={12}>
        <DefaultCard
          data-testid="card-body"
          disabled={disabled}
          title={title}
          smallSubtitle={subTitle}
        >
          <Grid container alignItems="center" justifyContent="center">
            {hasImageMime ? (
              <ImagePreview
                data-testid="photo-container"
                imageBase64={base64String}
                onDelete={onDelete}
                deleteButtonId={deleteButtonId}
                disabled={disabled}
                requestStatus={requestStatus}
              />
            ) : (
              hasPdfMime && (
                <PdfPreview
                  data-testid="pdf-container"
                  displayValue={fileName}
                  onDelete={onDelete}
                  deleteButtonId={deleteButtonId}
                  disabled={disabled}
                  requestStatus={requestStatus}
                />
              )
            )}
            {base64String === '' && uploadButton}
          </Grid>
          <Collapse
            data-testid="warning-card"
            in={showWarning || requestStatus === 'failure'}
            style={{ marginTop: 16 }}
          >
            {requestStatus === 'failure' ? (
              <MessageBox
                data-testid="error-message"
                variant="error"
                hideCloseButton
                message="No pudimos subir el archivo. Por favor, intenta nuevamente."
              />
            ) : (
              <MessageBox
                data-testid="warning-message"
                variant="warning"
                hideCloseButton
                message="El archivo supera el límite de tamaño (5 MB). Por favor, intenta con otro archivo."
              />
            )}
          </Collapse>
        </DefaultCard>
      </Grid>

      <ConfirmationDialog
        data-testid="confirmation-dialog"
        open={showConfirmation}
        message={confirmationMessage}
        confirmAction={handleDeleteConfirm}
        rejectAction={handleDeleteReject}
      />
    </Grid>
  );
};

export default PhotoCard;
